import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = [
    'form',
    'input',
    'select',
    'submit',
    'addressForm',
    'couponCode',
    'recipientField',
    'submitLabel',
    'submitSpinner'
  ]
  static values = {
    shippingMethod: String
  }

  connect() {
    this.shippingMethod = this.shippingMethodValue
    this.debouncedValidateOnInput = this.debounce(this.validateOnInput.bind(this), 300)
    this.isSubmitting = false
    this.loadingSubmitLabel = 'Processando...'
    this.defaultSubmitLabel = this.hasSubmitLabelTarget ? this.submitLabelTarget.textContent.trim() : null

    if (this.hasSubmitTarget) {
      this.submitTarget.disabled = !this.isValid(false)
    }

    if (this.hasAddressFormTarget) {
      this.addressFormTarget.addEventListener('fillAddressFields', this.fillAddressFields.bind(this))
    }
    this._externalValidateHandler = this.externalValidate.bind(this)
    this.element.addEventListener('cart:validate', this._externalValidateHandler)
  }

  disconnect() {
    if (this.hasAddressFormTarget) {
      this.addressFormTarget.removeEventListener('fillAddressFields', this.fillAddressFields.bind(this))
    }
    if (this._externalValidateHandler) {
      this.element.removeEventListener('cart:validate', this._externalValidateHandler)
    }
  }

  setCoupon() {
    const couponCode = this.couponCodeTarget.value

    fetch('/cart', {
      method: 'PATCH',
      headers: {
        'Accept': 'text/vnd.turbo-stream.html',
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
      },
      body: JSON.stringify({
        cart: {
          coupon_code: couponCode
        }
      })
    }).then(async response => {
      if (response.ok) {
        const html = await response.text()
        Turbo.renderStreamMessage(html)
      }
    }).catch(error => {
      console.error('There was a problem when trying to apply coupon', error)
    })
  }

  fillAddressFields(event) {
    const data = event.detail
    document.getElementById('address_street').textContent = `${data.street},`
    document.getElementById('address_neighborhood').textContent = `${data.neighborhood},`
    document.getElementById('address_city').textContent = `${data.city},`
    document.getElementById('address_state').textContent = data.state
  }

  debounce(fn, delay) {
    let timeout
    return (...args) => {
      clearTimeout(timeout)
      timeout = setTimeout(() => fn.apply(this, args), delay)
    }
  }

  validateOnInput(event) {
    this.validateField(event.target)
    this.toggleSubmit()
  }

  validateOnSelect(event) {
    this.validateSelectField(event.target)
    this.toggleSubmit()
  }

  validateField(input, flagVisually = true) {
    const value = input.value.trim()
    const minLength = input.hasAttribute('minlength') ? input.minLength : 1
    const isValid = value.length >= minLength

    if (isValid && flagVisually) {
      input.classList.remove('border-red-400')
      input.classList.add('border-gray-300')
    } else if (flagVisually) {
      input.classList.add('border-red-400')
      input.classList.remove('border-gray-300')
    }

    return isValid
  }

  validateSelectField(select, flagVisually = true) {
    const isValid = select.value !== ''

    if (isValid && flagVisually) {
      select.classList.remove('border-red-400')
      select.classList.add('border-gray-300')
    } else if (flagVisually) {
      select.classList.add('border-red-400')
      select.classList.remove('border-gray-300')
    }

    return isValid
  }

  toggleNumberValidation(event) {
    const addressWithoutNumber = event.target.checked
    const numberField = document.getElementById('address_number')
    const numberLabel = document.getElementById('label_number')
    const complementField = document.getElementById('address_complement')
    const complementLabel = document.getElementById('label_complement')

    if (addressWithoutNumber) {
      numberField.value = 'SN'
      numberField.disabled = true
      numberField.removeAttribute('data-cart-target')
      numberField.removeAttribute('data-action')
      complementField.setAttribute('data-cart-target', 'input')
      complementField.setAttribute('data-action', 'input->cart#validateOnInput')

      if (complementLabel) {
        complementLabel.classList.add('font-semibold')
        complementLabel.classList.remove('font-medium')
        complementLabel.textContent = 'Complemento*'
      }

      if (numberLabel) {
        numberLabel.classList.remove('font-semibold')
        numberLabel.classList.add('font-medium')
        numberLabel.textContent = 'Número'
      }
    } else {
      numberField.value = ''
      numberField.disabled = false
      numberField.setAttribute('data-cart-target', 'input')
      numberField.setAttribute('data-action', 'input->cart#validateOnInput')
      complementField.removeAttribute('data-cart-target')
      complementField.removeAttribute('data-action')

      if (complementLabel) {
        complementLabel.classList.remove('font-semibold')
        complementLabel.classList.add('font-medium')
        complementLabel.textContent = 'Complemento'
      }

      if (numberLabel) {
        numberLabel.classList.add('font-semibold')
        numberLabel.classList.remove('font-medium')
        numberLabel.textContent = 'Número*'
      }
    }

    this.toggleSubmit()
  }

  toggleRecipientFields(event) {
    const isChecked = event.target.checked
    const recipientFields = this.recipientFieldTargets

    recipientFields.forEach(field => {
      const input = field.querySelector('input')

      if (isChecked) {
        field.classList.remove('hidden')
        input.dataset.cartTarget = 'input'
      } else {
        field.classList.add('hidden')
        delete(input.dataset.cartTarget)
      }
    })

    this.toggleSubmit()
  }

  updateShippingMethod(event) {
    this.shippingMethod = event.target.value
    this.toggleSubmit()
  }

  updatePaymentMethod(event) {
    const paymentMethod = event.target.value

    if (this.hasPaymentMethodTarget) {
      this.paymentMethodTarget.value = paymentMethod
    }

    fetch('/cart', {
      method: 'PATCH',
      headers: {
        'Accept': 'text/vnd.turbo-stream.html',
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
      },
      body: JSON.stringify({
        cart: {
          payment_method: paymentMethod
        }
      })
    }).then(async response => {
      if (response.ok) {
        const html = await response.text()
        Turbo.renderStreamMessage(html)
      }
    }).catch(error => {
      console.error('There was a problem when trying to apply coupon', error)
    })
  }

  isValid(flagFields = true) {
    let valid = true
    const inputs = Array.from(this.element.querySelectorAll('[data-cart-target="input"]'))
    const selects = Array.from(this.element.querySelectorAll('[data-cart-target="select"]'))

    inputs.forEach(field => {
      if (!this.isFieldActive(field)) {
        return
      }

      if (!this.validateField(field, flagFields)) {
        valid = false
      }
    })

    selects.forEach(field => {
      if (!this.isFieldActive(field)) {
        return
      }

      if (!this.validateSelectField(field, flagFields)) {
        valid = false
      }
    })

    return valid && this.shippingMethod
  }

  isFieldActive(field) {
    if (!field) return false
    if (field.disabled) return false
    if (field.type === 'hidden') return false

    const styles = window.getComputedStyle(field)

    if (styles.display === 'none' || styles.visibility === 'hidden' || Number(styles.opacity) === 0) {
      return false
    }

    if (field.offsetParent === null && styles.position !== 'fixed') {
      return false
    }

    return true
  }

  toggleSubmit() {
    if (!this.hasSubmitTarget) return

    const isValid = this.isValid(false)
    const tooltipContainer = this.submitTarget.parentElement
    this.submitTarget.disabled = this.isSubmitting || !isValid

    if (this.isSubmitting || isValid) {
      tooltipContainer.removeAttribute('data-controller')
      return
    }

    tooltipContainer.setAttribute('data-controller', 'tooltip')
  }

  externalValidate() {
    if (!this.hasSubmitTarget) return

    this.toggleSubmit()
  }

  handleSubmitStart() {
    if (!this.hasSubmitTarget) return
    if (this.isSubmitting) return

    this.isSubmitting = true
    this.showSubmitSpinner()
  }

  handleSubmitEnd() {
    if (!this.hasSubmitTarget) return

    this.isSubmitting = false
    this.hideSubmitSpinner()
    this.toggleSubmit()
  }

  showSubmitSpinner() {
    if (!this.hasSubmitTarget) return

    this.submitTarget.disabled = true
    this.submitTarget.setAttribute('aria-busy', 'true')

    if (this.hasSubmitSpinnerTarget) {
      this.submitSpinnerTarget.classList.remove('hidden')
    }

    if (this.hasSubmitLabelTarget) {
      const label = this.loadingSubmitLabel || 'Processando...'
      this.submitLabelTarget.textContent = label
    }
  }

  hideSubmitSpinner() {
    if (!this.hasSubmitTarget) return

    this.submitTarget.removeAttribute('aria-busy')

    if (this.hasSubmitSpinnerTarget) {
      this.submitSpinnerTarget.classList.add('hidden')
    }

    if (this.hasSubmitLabelTarget) {
      const defaultLabel = this.defaultSubmitLabel || 'Finalizar pagamento'
      this.submitLabelTarget.textContent = defaultLabel
    }
  }
}
